import React from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { CollegeVisitContrastList } from '@backrs_ui/grow-ui/src/pages/CollegeVisitGuide/content';

import { Box, SxProps, Theme, Typography } from '@mui/material';

type ContrastListsProps = Readonly<{
  idPrefix: string;
  positive: CollegeVisitContrastList;
  negative: CollegeVisitContrastList;
}>;

/** Two side-by-side lists headed by a colored chip: positive items with check marks, negative with crosses. */
export const ContrastLists: React.FC<ContrastListsProps> = ({ idPrefix, positive, negative }) => (
  <Box sx={gridSx}>
    <ContrastCard id={`${idPrefix}-positive`} list={positive} tone="positive" />
    <ContrastCard id={`${idPrefix}-negative`} list={negative} tone="negative" />
  </Box>
);

type ContrastCardProps = Readonly<{
  id: string;
  list: CollegeVisitContrastList;
  tone: 'positive' | 'negative';
}>;

const ContrastCard: React.FC<ContrastCardProps> = ({ id, list, tone }) => {
  const isPositive = tone === 'positive';

  return (
    <Box id={id} sx={columnSx}>
      <Typography variant="text-xs-semibold" sx={isPositive ? positiveChipSx : negativeChipSx}>
        {list.label}
      </Typography>
      {list.title && (
        <Typography variant="text-md-semibold" sx={titleSx}>
          {list.title}
        </Typography>
      )}
      <Box component="ul" sx={listSx}>
        {list.items.map((item) => (
          <Box component="li" key={item} sx={itemSx}>
            <Box sx={iconSx}>
              <AppIcon
                iconName={isPositive ? 'Check' : 'XClose'}
                diameter="16px"
                color={
                  isPositive
                    ? 'Colors/Foreground/fg-success-primary'
                    : 'Colors/Foreground/fg-error-primary'
                }
              />
            </Box>
            <Typography variant="text-sm-regular" sx={itemTextSx}>
              {item}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-xl'],
  '@container (min-width: 700px)': {
    gridTemplateColumns: '1fr 1fr',
  },
});

const columnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
});

const chipBaseSx = ({ backrs }: Theme) => ({
  px: backrs.spacing['spacing-lg'],
  py: backrs.spacing['spacing-xs'],
  borderRadius: backrs.radius['radius-full'],
});

const positiveChipSx: SxProps<Theme> = (theme) => ({
  ...chipBaseSx(theme),
  color: theme.backrs.colors['Colors/Text/text-success-primary'],
  backgroundColor: theme.backrs.colors['Colors/Background/bg-success-primary'],
  border: `1px solid ${theme.backrs.colors['Colors/Border/border-secondary']}`,
});

const negativeChipSx: SxProps<Theme> = (theme) => ({
  ...chipBaseSx(theme),
  color: theme.backrs.colors['Colors/Text/text-error-primary'],
  backgroundColor: theme.backrs.colors['Colors/Background/bg-error-primary'],
  border: `1px solid ${theme.backrs.colors['Colors/Border/border-error-subtle']}`,
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const listSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  m: 0,
  p: 0,
  listStyle: 'none',
});

const itemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
});

const iconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexShrink: 0,
  pt: backrs.spacing['spacing-xxs'],
});

const itemTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});
